import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { WorkspaceFolder } from 'vscode-languageserver';
import { Utils } from 'vscode-uri';
import { AbstractAIContextProvider } from '../ai_context_provider';
import { LruCache } from '../../advanced_context/lru_cache';
import { LRU_CACHE_BYTE_SIZE_LIMIT } from '../../advanced_context/helpers';
import { DuoProjectAccessChecker } from '../../services/duo_access';
import { DuoProjectStatus } from '../../services/duo_access/project_access_checker';
import { log } from '../../log';
import { DuoProject } from '../../services/duo_access/project_access_cache';
import { parseURIString } from '../../services/fs/utils';
import { AIContextItem, AIContextItemMetadata, AIContextSearchQuery } from '..';

type OpenTabMetadata = AIContextItemMetadata & {
  enabled: boolean;
  disabledReasons?: string[];
  subType: 'open_tab';
  iid?: string;
  title?: string;
  description?: string;
  relativePath?: string;
  workspaceFolder?: WorkspaceFolder;
  project?: string;
};

export type OpenTabAIContextItem = AIContextItem & {
  category: 'file';
  metadata: OpenTabMetadata;
};

export interface OpenTabContextProvider extends AbstractAIContextProvider<OpenTabAIContextItem> {}

export const OpenTabContextProvider =
  createInterfaceId<OpenTabContextProvider>('OpenTabContentProvider');

@Injectable(OpenTabContextProvider, [DuoProjectAccessChecker])
export class DefaultOpenTabContextProvider
  extends AbstractAIContextProvider<OpenTabAIContextItem>
  implements OpenTabContextProvider
{
  #projectAccessChecker: DuoProjectAccessChecker;

  #lruCache: LruCache;

  constructor(projectAccessChecker: DuoProjectAccessChecker) {
    super('open_tab');
    this.#projectAccessChecker = projectAccessChecker;
    this.#lruCache = LruCache.getInstance(LRU_CACHE_BYTE_SIZE_LIMIT);
  }

  async searchContextItems(query: AIContextSearchQuery): Promise<OpenTabAIContextItem[]> {
    if (query.query.trim() !== '') {
      return [];
    }
    const openFiles = this.#lruCache.mostRecentFiles({});

    const itemsWithContent = [] as OpenTabAIContextItem[];

    log.debug(`Advanced Context: context item search for ${this.type}`);
    openFiles.reduce((result, file) => {
      let enabled = true;
      let project: DuoProject | undefined;
      if (file.workspaceFolder) {
        const { project: projectFromChecker, status } =
          this.#projectAccessChecker.checkProjectStatus(file.uri, file.workspaceFolder);
        enabled = status !== DuoProjectStatus.DuoDisabled;
        project = projectFromChecker;
      }
      const item = {
        id: file.uri,
        category: 'file' as const,
        metadata: {
          title: Utils.basename(parseURIString(file.uri)),
          project: project?.namespaceWithPath ?? 'not a GitLab project',
          enabled,
          disabledReasons: enabled ? [] : ['project disabled'],
          subType: 'open_tab' as const,
          relativePath: file.fileRelativePath,
          workspaceFolder: file.workspaceFolder ?? { name: '', uri: '' },
        },
      } satisfies OpenTabAIContextItem;

      log.debug(`Advanced Context: open tab context item ${item.id} was found`);
      result.push(item);

      return result;
    }, itemsWithContent);

    return Promise.resolve(itemsWithContent);
  }

  async retrieveSelectedContextItemsWithContent(): Promise<OpenTabAIContextItem[]> {
    const items = this.getSelectedContextItems();

    return items.map((document) => {
      log.debug(
        `Advanced Context: open tab context item ${document.id} was retreived with content`,
      );
      const itemFile = this.#lruCache.openFiles.get(document.id);
      if (itemFile) {
        // eslint-disable-next-line no-param-reassign
        document.content = itemFile.prefix + itemFile.suffix;
      }
      return document;
    });
  }
}
